<template>
  <form @submit.prevent>
    <section class="channel-config__container">
      <CCategory :channel="currentChannel" :disabled="disableMergedFields" />

      <CMemberSelect
        v-if="!currentChannel.id"
        @change-member="handleChangeMember"
      />

      <CMergeChannelSelect
        v-if="isChat"
        :merge-channel-id="mergeChannel && mergeChannel.id"
        :person-id="controlChannel.personId"
        :has-demands="hasDemands"
        @merge-channel="handleMergeChannel"
      />

      <CRenameChannelInput
        v-else
        :channel-name="controlChannel.name"
        :classifier="controlChannel.subCategoryClassifier"
        @change-name="handleChangeName"
      />

      <CTagsSelect
        :channel-tags="currentChannel.tags"
        @change-tags="handleChangeTags"
      />

      <CAssessementSelect
        v-if="currentChannel.id"
        :assessment="assessment"
        :channel-id="currentChannel.id"
        :disabled="!isChat || disableMergedFields"
        @change-assessment="handleChangeAssessment"
      />

      <CStaffSelect
        :channel-id="currentChannel.id"
        :channel-name="currentChannel.name"
        :channel-staff="currentChannel.staff"
        :disabled="disableMergedFields"
        @add-staff="handleAddStaff"
        @remove-staff="handleRemoveStaff"
      />

      <CallOut
        v-if="isDischarge && !disableMergedFields"
        variant="info"
        title-icon="icInfo"
        title="Sem critérios clínicos para seguimento"
        description="Baseado na demanda, considere encerrar o canal após concluir o atendimento."
        primary-button-label="Entenda os critérios"
        @on-click-primary="openBud"
      />

      <CConfigActions
        :is-discharge="isDischarge"
        :is-saving="isSaving"
        :show-archive-button="showArchiveButton"
        @on-rename-channel="handleRenameChannelAction"
        @on-create-channel="handleCreateChannelAction"
        @on-merge-channel="handleMergeChannelAction"
        @on-leave-channel="handleLeaveChannelAction"
        @on-archive-channel="handleArchiveChannelAction"
        @on-close-configuration="leaveChannel"
        @on-discharge-channel="handleDischargeChannel"
      />
    </section>
  </form>
  <div>
    <CArchiveAdministrativeChannelDialog
      v-if="showArchiveAdministrativeChannelDialog"
      :opened="showArchiveAdministrativeChannelDialog"
      :is-loading-suggested-theme="isLoadingSuggestedTheme"
      :suggested-theme="suggestedTheme"
      :contact-themes="contactThemes"
      @load-contact-themes="loadContactThemes"
      @on-close="handleArchiveAdministrativeChannel"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { CallOut } from '@/components/shared/atoms'
import {
  CHANNEL_CATEGORIES,
  CHANNEL_KINDS,
  CHANNEL_SUBCATEGORIES
} from '@/models/channels'
import { configTypes } from '@/models/featureConfig'
import { toISO861 } from '@/utils/date'

import {
  CArchiveAdministrativeChannelDialog,
  CAssessementSelect,
  CCategory,
  CConfigActions,
  CMemberSelect,
  CMergeChannelSelect,
  CRenameChannelInput,
  CStaffSelect,
  CTagsSelect
} from '../molecules'

export default {
  components: {
    CArchiveAdministrativeChannelDialog,
    CAssessementSelect,
    CMemberSelect,
    CTagsSelect,
    CStaffSelect,
    CMergeChannelSelect,
    CRenameChannelInput,
    CConfigActions,
    CallOut,
    CCategory
  },
  inject: ['confirm', 'notify'],
  emits: ['openBudPillar'],
  data() {
    return {
      showModalLeaveAndChangeOwnerChannel: false,
      isSaving: false,
      isContactThemeRedirectionEnabled: false,
      showArchiveAdministrativeChannelDialog: false,
      isLoadingSuggestedTheme: false,
      suggestedTheme: '',
      akinatorSetupId: undefined,
      contactThemes: undefined
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('pillars/channel', ['controlChannel', 'mergeChannel']),
    showArchiveButton() {
      return this.controlChannel.kind !== CHANNEL_KINDS.CHAT
    },
    assessment() {
      return this.currentChannel.screening?.assessment
    },
    isChat() {
      return this.controlChannel.kind === CHANNEL_KINDS.CHAT
    },
    currentChannel() {
      return this.mergeChannel?.id ? this.mergeChannel : this.controlChannel
    },
    disableMergedFields() {
      return !!this.mergeChannel?.id
    },
    isCreate() {
      return !this.controlChannel?.id
    },
    hasDemands() {
      return !!(
        (this.controlChannel.kind === CHANNEL_KINDS.CHAT &&
          this.currentChannel.demands &&
          this.currentChannel.demands.length) ||
        this.currentChannel.category === CHANNEL_CATEGORIES.ADMINISTRATIVE
      )
    },
    isDischarge() {
      return !!(
        this.controlChannel.kind === CHANNEL_KINDS.CHAT &&
        this.controlChannel.discharge
      )
    },
    isAdministrativeChannel() {
      return this.controlChannel.category === CHANNEL_CATEGORIES.ADMINISTRATIVE
    }
  },
  mounted() {
    this.getFeatureFlags()
  },
  methods: {
    handleChangeMember(member) {
      const { channelPersonId, id: personId } = member || {}
      this.$store.dispatch('pillars/channel/setMember', {
        channelPersonId,
        personId
      })
    },
    handleChangeName(name) {
      this.$store.dispatch('pillars/channel/renameChannel', {
        channelId: this.controlChannel.id,
        name,
        personId: this.controlChannel.personId
      })
    },
    handleChangeTags(tags) {
      this.$store.dispatch('pillars/channel/updateChannelTags', {
        channelId: this.currentChannel.id,
        tags
      })
    },
    handleChangeAssessment(assessment) {
      this.$store.dispatch('pillars/channel/updateChannelAssessment', {
        channelId: this.currentChannel.id,
        assessment
      })
    },
    handleAddStaff({ staffId, staff }) {
      this.handleStaffChange({ staffId, staff, add: true })
    },
    handleRemoveStaff({ staffId, staff }) {
      this.handleStaffChange({ staffId, staff, add: false })
    },
    handleStaffChange(data) {
      this.$store.dispatch('pillars/channel/updateStaffs', {
        ...data,
        channelId: this.currentChannel.id
      })
    },
    handleMergeChannel(selectedChannel) {
      this.$store.dispatch(
        'pillars/channel/updateMergedChannel',
        selectedChannel
      )
    },
    openBud() {
      this.$emit('openBudPillar', this.controlChannel.discharge)
    },
    async handleRenameChannelAction() {
      this.isSaving = true
      const isRenamed = await this.$store.dispatch(
        'pillars/channel/renameChannel',
        {
          ...this.controlChannel,
          channelId: this.controlChannel.id
        }
      )
      if (isRenamed) {
        this.closeConfig()
      }
      this.isSaving = false
    },
    async handleCreateChannelAction() {
      if (!this.controlChannel.name) {
        this.notify({
          description: 'É obrigatório preencher o nome do canal!',
          duration: 3000
        })
        return
      }

      this.isSaving = true

      const channel = await this.$store.dispatch(
        'pillars/channel/createChannel',
        {
          ...this.controlChannel,
          staffIds: Object.keys(this.controlChannel.staff)
        }
      )

      this.openChannel({
        id: channel.id,
        personId: this.controlChannel.personId
      })
    },
    async handleMergeChannelAction() {
      const confirmed = await this.confirm({
        title: 'Mesclar canal',
        description:
          'Você vai juntar a conversa para o canal já existente. Você não poderá ver o conteúdo anterior do canal.',
        ok: 'Mesclar canal'
      })

      if (!confirmed) return

      this.isSaving = true
      const merged = await this.$store.dispatch(
        'pillars/channel/mergeChannels',
        {
          currentChannelId: this.controlChannel.id,
          mergeChannelId: this.mergeChannel.id
        }
      )
      this.isSaving = false
      if (merged) {
        this.openChannel(this.mergeChannel)
      }
    },
    async handleArchiveChannelAction() {
      if (
        this.isAdministrativeChannel &&
        this.isContactThemeRedirectionEnabled
      ) {
        this.isLoadingSuggestedTheme = true
        this.showArchiveAdministrativeChannelDialog = true
        return
      }

      const confirmed = await this.confirm({
        warning: true,
        title: 'Encerrar canal',
        description:
          'Uhul! Canal encerrado é membro saudável! Lembre-se: Você não pode reabrir um canal depois que encerrado.',
        ok: 'Encerrar Canal'
      })

      if (!confirmed) return

      this.isSaving = true
      const result = await this.$store.dispatch(
        'pillars/channel/archiveChannel',
        this.controlChannel.id
      )
      this.isSaving = false
      if (!result) return

      if (
        this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.VIRTUAL_CLINIC
      ) {
        this.$router.push('/virtualclinic')
        return
      }
      this.$router.push('/pillars')
    },
    async handleLeaveChannelAction() {
      const confirmed = await this.confirm({
        title: 'Sair da conversa',
        description:
          'Quer sair da conversa? Você não terá mais acesso à visualização, nem poderá mandar mais mensagens.',
        ok: 'Sair da Conversa'
      })

      if (!confirmed) return

      const removed = await this.$store.dispatch(
        'pillars/channel/removeStaffFromChannel',
        {
          channelId: this.controlChannel.id,
          staffId: this.user.id
        }
      )
      if (removed) {
        this.leaveChannel()
      }
    },
    async handleDischargeChannel() {
      this.isSaving = true
      const isRenamed = await this.$store.dispatch(
        'pillars/channel/dischargeChat',
        this.controlChannel
      )
      if (isRenamed) {
        this.leaveChannel()
      }
      this.isSaving = false
    },
    async handleArchiveAdministrativeChannel({
      detail,
      dialogOpenedAt,
      selectedCategoryTheme,
      selectedContactReason
    }) {
      this.showArchiveAdministrativeChannelDialog = false
      if (detail !== 'confirm') {
        this.suggestedTheme = ''
      } else {
        const result = await this.$store.dispatch(
          'pillars/channel/classifyAndArchiveChannel',
          {
            channelId: this.controlChannel.id,
            akinatorSetupId: this.akinatorSetupId,
            suggestedTheme: this.suggestedTheme,
            theme: selectedCategoryTheme,
            reason: selectedContactReason,
            screenOpenedAt: toISO861(dialogOpenedAt)
          }
        )
        if (!result) return
        this.notify({
          icon: 'icCheckOutlined',
          duration: 3000,
          description: 'Canal encerrado com sucesso!'
        })
        this.$router.push('/pillars')
      }
    },
    async openChannel(channel) {
      this.closeConfig()
      if (this.$route.query.channelId === channel.id) {
        return
      }
      await this.$router.push(
        `/member/${channel.personId}/pillars?channelId=${channel.id}`
      )
    },
    closeConfig() {
      this.isSaving = false
      this.$store.commit('pillars/channel/setControlChannel')
      this.$store.commit('pillars/channel/setMergeChannel')
      this.$store.commit('ui/channel/resetChannelsUI')
    },
    async leaveChannel() {
      await this.$store.dispatch('pillars/channel/clearState')
      const query = { ...this.$route.query }
      delete query.channelId
      this.$router.replace({ query })
      this.closeConfig()
    },
    async getFeatureFlags() {
      this.isContactThemeRedirectionEnabled = await this.$store.dispatch(
        'featureConfig/getFeatureFlagById',
        {
          id: 'ehr-is_contact_theme_redirection_enabled',
          type: configTypes.BOOLEAN
        }
      )
    },
    async loadContactThemes() {
      this.contactThemes = await this.$store.dispatch(
        'pillars/channel/getAllContactThemes'
      )
      const response = await this.$store.dispatch(
        'pillars/channel/getAkinatorSuggestedTheme',
        this.controlChannel.id
      )
      if (response) {
        this.akinatorSetupId = response.akinatorSetupId
        this.suggestedTheme = response.categoryTheme
      }

      this.isLoadingSuggestedTheme = false
    }
  }
}
</script>

<style>
.channel-config__container {
  display: grid;
  gap: var(--gl-spacing-04);
  padding: var(--gl-spacing-04);
}
</style>
